/**
 * GA4 イベント送信ヘルパー.
 * window.gtag が無い (CI/local) 環境では no-op で安全に通過する.
 * イベント命名は GA4 標準推奨カスタムイベントに準拠 (snake_case).
 */

type GtagParams = Record<string, string | number | boolean | undefined>;

function callGtag(eventName: string, params: GtagParams): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as {
    gtag?: (cmd: 'event', action: string, params: Record<string, unknown>) => void;
  };
  if (typeof w.gtag !== 'function') return;
  w.gtag('event', eventName, params);
}

/** 概要資料 PDF のダウンロード. 既に DeckDownloadCTA で使用中. */
export const trackDeckDownload = (placement: string) => {
  callGtag('download_deck', { event_category: 'lead', event_label: placement, placement });
};

/** ログイン画面に到達 (新規/既存問わずファネル入口). */
export const trackSignupStarted = (source?: string) => {
  callGtag('signup_started', { event_category: 'conversion', source: source || 'unknown' });
};

/**
 * Magic Link 認証完了 → ダッシュボード初回到達.
 * 1ユーザ複数回発火する可能性あり (毎回ログイン時). 初回判定は新規/既存org の有無で代替.
 */
export const trackSignupCompleted = (params: { isNewOrg: boolean; userId: string }) => {
  callGtag('signup_completed', {
    event_category: 'conversion',
    is_new_org: params.isNewOrg,
    user_id: params.userId,
  });
};

/** Worker が報告を投稿成功. */
export const trackCaseSubmitted = (params: {
  qrPointId?: string;
  category?: string;
  riskLevel?: string;
  hasAudio?: boolean;
  hasImage?: boolean;
}) => {
  callGtag('case_submitted', {
    event_category: 'engagement',
    qr_point_id: params.qrPointId || 'unknown',
    category: params.category || 'unknown',
    risk_level: params.riskLevel || 'unknown',
    has_audio: params.hasAudio || false,
    has_image: params.hasImage || false,
  });
};

/** 管理者がケースを「解決済み」にマーク. PDCA の Check/Action 完遂指標. */
export const trackCaseResolved = (params: {
  caseId: string;
  category?: string;
  riskLevel?: string;
  effectiveness?: string;
}) => {
  callGtag('case_resolved', {
    event_category: 'engagement',
    case_id: params.caseId,
    category: params.category || 'unknown',
    risk_level: params.riskLevel || 'unknown',
    effectiveness: params.effectiveness || 'unknown',
  });
};

/** Stripe Checkout ボタンクリック (まだ決済前). 課金ファネル入口. */
export const trackCheckoutStarted = (params: { tier: string; organizationId: string }) => {
  callGtag('checkout_started', {
    event_category: 'conversion',
    tier: params.tier,
    organization_id: params.organizationId,
  });
};

/** Stripe Checkout 戻り URL で session_id を確認できたとき (=決済成功). */
export const trackSubscriptionStarted = (params: { tier: string; sessionId: string }) => {
  callGtag('subscription_started', {
    event_category: 'conversion',
    tier: params.tier,
    session_id: params.sessionId,
  });
};
